import React, { useContext, useState } from 'react';
import {
  Typography,
  TableContainer,
  Paper,
  Table,
  TableHead,
  Box,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
  Collapse,
  Modal,
  Backdrop,
  Button,
  TableSortLabel,
  useTheme
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import DeleteIcon from '@mui/icons-material/DeleteForever';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import Pagination from '@mui/material/Pagination';
import SessionContext from '../../../../contexts/SessionContext';
import Loading from '../../../../components/common/Loading';

const formatAuditDate = (value) => {
  if (!value) {
    return 'Não disponível';
  }

  /*
   * O backend atualmente retorna timestamps sem timezone explícito.
   * Esses timestamps são gerados pelo banco em UTC.
   *
   * Caso já venha timezone/Z, preservamos.
   * Caso contrário, adicionamos Z para interpretar corretamente como UTC
   * antes de converter para horário de Brasília.
   */
  const normalizedValue = /[zZ]|[+-]\d\d:\d\d$/.test(value)
    ? value
    : `${value}Z`;

  const date = new Date(normalizedValue);

  if (Number.isNaN(date.getTime())) {
    return 'Não disponível';
  }

  return date.toLocaleString('pt-BR', {
    timeZone: 'America/Sao_Paulo',
  });
};

const WorksTable = (props) => {
  const theme = useTheme();
  const order = props.order || 'desc';
  const orderBy = props.orderBy || 'id';

  let { hasRole } = useContext(SessionContext);

  // Mapeamento entre headers e campos dos dados (suporta PT e EN)
  // IMPORTANTE: Os nomes aqui devem corresponder aos campos do backend
  // e não aos campos do JSON retornado.
  const getFieldName = (headerTitle) => {
    const headerMap = {
      // Português
      'ORB code': 'orb_code',
      'Obra': 'title',
      '% de controle ORB': 'control_percent',

      // Inglês
      'Work': 'title',
      'ORB Control %': 'control_percent',
    };

    return headerMap[headerTitle] || null;
  };

  // Handler para mudança de ordenação - comunica ao componente pai
  const handleRequestSort = (property) => {
    if (!props.onSortChange) {
      return;
    }

    const isAsc = orderBy === property && order === 'asc';
    const newOrder = isAsc ? 'desc' : 'asc';
    const newOrderBy = property;

    props.onSortChange(newOrderBy, newOrder);
  };

  return (
    <>
      <TableContainer
        className="table-container"
        component={Paper}
      >
        <Table
          size="small"
          aria-label="collapsible table"
          className="basic-table"
        >
          <TableHead className="basic-table-head">
            <TableRow>
              <TableCell></TableCell>

              {props.headers_table.map((title, index) => {
                const fieldName = getFieldName(title);
                const isSortable = fieldName !== null;

                return (
                  <TableCell
                    key={`${title}-${index}`}
                    align="center"
                    sortDirection={
                      orderBy === fieldName ? order : false
                    }
                  >
                    {isSortable ? (
                      <TableSortLabel
                        active={orderBy === fieldName}
                        direction={
                          orderBy === fieldName
                            ? order
                            : 'asc'
                        }
                        onClick={() =>
                          handleRequestSort(fieldName)
                        }
                        sx={{
                          '&.Mui-active': {
                            color:
                              theme.palette.secondary.main,
                          },
                          '& .MuiTableSortLabel-icon': {
                            color:
                              orderBy === fieldName
                                ? `${theme.palette.secondary.main} !important`
                                : 'rgba(255, 255, 255, 0.54)',
                          },
                          '&:hover': {
                            color:
                              theme.palette.secondary.light,
                          },
                          '&:hover .MuiTableSortLabel-icon': {
                            color:
                              `${theme.palette.secondary.light} !important`,
                          },
                          color:
                            orderBy === fieldName
                              ? theme.palette.secondary.main
                              : 'inherit',
                        }}
                      >
                        {title}
                      </TableSortLabel>
                    ) : (
                      title
                    )}
                  </TableCell>
                );
              })}

              <TableCell align="center">
                Data de cadastro
              </TableCell>

              <TableCell align="center">
                Última alteração
              </TableCell>

              {hasRole([
                'WORKS_FULL_VIEW',
                'WORKS_WRITE',
              ]) && (
                <TableCell align="center"></TableCell>
              )}
            </TableRow>
          </TableHead>

          <TableBody>
            {props.works?.length > 0
              ? props.works.map((work, index) => {
                  return (
                    <WorkRow
                      key={work.id ?? index}
                      index={index}
                      split_headers={
                        props.split_headers
                      }
                      clickable={props.clickable}
                      onRowClick={
                        props.showWorkForm
                      }
                      work={work}
                      translateCategory={
                        props.translateCategory
                      }
                      deleteWork={props.deleteWork}
                      hasRole={hasRole}
                      loading={props.deletingWork}
                    />
                  );
                })
              : null}
          </TableBody>
        </Table>
      </TableContainer>

      <Pagination
        shape="rounded"
        className="table-pagination"
        count={props.pages}
        page={props.currentPage}
        onChange={(e, value) => {
          props.onPageChange(value);
        }}
      />
    </>
  );
};

export default WorksTable;

function WorkRow(props) {
  const [collapsed, setCollapsed] = useState(true);

  const [
    showConfirmationPanel,
    setShowConfirmationPanel,
  ] = useState(false);

  const hasWriteAccess = props.hasRole([
    'WORKS_FULL_VIEW',
    'WORKS_WRITE',
  ]);

  return (
    <>
      <TableRow className="row">
        <TableCell
          key={props.index}
          className="cell"
        >
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={() => {
              setCollapsed(!collapsed);
            }}
          >
            {collapsed ? (
              <KeyboardArrowDownIcon />
            ) : (
              <KeyboardArrowUpIcon />
            )}
          </IconButton>
        </TableCell>

        {hasWriteAccess ? (
          <>
            <TableCell
              className="cell clickable"
              align="center"
              onClick={() =>
                props.onRowClick(props.work)
              }
            >
              {props.work.orb_code}{' '}
            </TableCell>

            <TableCell
              className="cell clickable"
              align="center"
              onClick={() =>
                props.onRowClick(props.work)
              }
            >
              {props.work.title}{' '}
            </TableCell>

            <TableCell
              className="cell clickable"
              align="center"
              onClick={() =>
                props.onRowClick(props.work)
              }
            >
              {props.work.orb_control}
            </TableCell>

            <TableCell
              className="cell"
              align="center"
            >
              {formatAuditDate(
                props.work.created_at
              )}
            </TableCell>

            <TableCell
              className="cell"
              align="center"
            >
              {formatAuditDate(
                props.work.updated_at
              )}
            </TableCell>

            <TableCell
              className="cell"
              align="center"
            >
              <DeleteIcon
                className="clickable deleteIcon"
                fontSize="small"
                onClick={() => {
                  setShowConfirmationPanel(true);
                }}
              />
            </TableCell>
          </>
        ) : props.hasRole([
            'WORKS_READ',
            'WORKS_CLIENT_VIEW',
          ]) ? (
          <>
            <TableCell
              align="center"
              className="cell"
            >
              {props.work.orb_code}{' '}
            </TableCell>

            <TableCell
              align="center"
              className="cell"
            >
              {props.work.title}{' '}
            </TableCell>

            <TableCell
              align="center"
              className="cell"
            >
              {props.work.orb_control}
            </TableCell>

            <TableCell
              align="center"
              className="cell"
            >
              {formatAuditDate(
                props.work.created_at
              )}
            </TableCell>

            <TableCell
              align="center"
              className="cell"
            >
              {formatAuditDate(
                props.work.updated_at
              )}
            </TableCell>
          </>
        ) : null}
      </TableRow>

      <TableRow>
        <TableCell
          style={{
            paddingBottom: 0,
            paddingTop: 0,
          }}
          colSpan={hasWriteAccess ? 7 : 6}
        >
          <Collapse
            timeout="auto"
            in={!collapsed}
            unmountOnExit
          >
            <Typography
              variant="h6"
              gutterBottom
              component="div"
            >
              {' '}
              Split{' '}
            </Typography>

            <Box margin={1}>
              <Table
                className="split-table"
                size="small"
                aria-label="purchases"
              >
                <TableHead>
                  <TableRow>
                    {props.split_headers.map(
                      (title, idx) => (
                        <TableCell
                          align="center"
                          key={`${title}-${idx}`}
                        >
                          {title}
                        </TableCell>
                      )
                    )}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {props.work.contributions?.map(
                    (contrib, idx) => {
                      return (
                        <TableRow
                          key={
                            contrib.id ?? idx
                          }
                        >
                          <TableCell
                            className="cell"
                            align="center"
                          >
                            {
                              contrib.contributor
                                .data.name
                            }

                            {contrib.contributor
                              .data.artist_name
                              ? ` (${contrib.contributor.data.artist_name})`
                              : null}
                          </TableCell>

                          <TableCell
                            className="cell"
                            align="center"
                          >
                            {
                              contrib.contributor
                                .share
                            }
                          </TableCell>

                          <TableCell
                            className="cell"
                            align="center"
                          >
                            {
                              contrib.contributor
                                .orb_fee
                            }
                          </TableCell>

                          {contrib.publisher !==
                          undefined ? (
                            <>
                              <TableCell
                                className="cell"
                                align="center"
                              >
                                {
                                  contrib
                                    .publisher
                                    .data.name
                                }
                              </TableCell>

                              <TableCell
                                className="cell"
                                align="center"
                              >
                                {
                                  contrib
                                    .publisher
                                    .share
                                }
                              </TableCell>

                              <TableCell
                                className="cell"
                                align="center"
                              >
                                {
                                  contrib
                                    .publisher
                                    .orb_fee
                                }
                              </TableCell>
                            </>
                          ) : (
                            <>
                              <TableCell
                                className="cell"
                                align="center"
                              >
                                {''}
                              </TableCell>

                              <TableCell
                                className="cell"
                                align="center"
                              >
                                {''}
                              </TableCell>

                              <TableCell
                                className="cell"
                                align="center"
                              >
                                {''}
                              </TableCell>
                            </>
                          )}

                          <TableCell
                            className="cell"
                            align="center"
                          >
                            {contrib.is_orb_managed ? (
                              <CheckIcon color="success" />
                            ) : null}
                          </TableCell>
                        </TableRow>
                      );
                    }
                  )}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>

      <Modal
        open={showConfirmationPanel}
        onClose={() =>
          setShowConfirmationPanel(false)
        }
        slots={{
          backdrop: Backdrop,
        }}
        slotProps={{
          backdrop: {
            timeout: 500,
          },
        }}
      >
        <div className="modal-container confirmation-panel">
          <Typography>
            Tem certeza que deseja deletar a obra{' '}
            {props.work.title}
          </Typography>

          {props.loading ? (
            <Loading text="Deletando faixa..." />
          ) : (
            <div className="buttons-container">
              <Button
                variant="contained"
                color="secondary"
                onClick={() => {
                  props.deleteWork(
                    props.work.id
                  );
                }}
              >
                Deletar
              </Button>

              <Button
                variant="contained"
                color="default"
                onClick={() => {
                  setShowConfirmationPanel(
                    false
                  );
                }}
              >
                Cancelar
              </Button>
            </div>
          )}
        </div>
      </Modal>
    </>
  );
}
